import type { Class, ClassTeacherCandidate } from "../../services/studentService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Classes and streams: how full each class is, each level's streams added up,
 * and which teachers lead a class. Classes arrive in school order (Baby, Middle,
 * Top, P1 ... P7, then stream), so "School order" keeps the order they came in.
 */

/** "P1" + "A" → "P1 A"; a level with no streams is just "P1". */
export const generatedName = (level: string, section?: string | null) => {
  const stream = (section ?? "").trim();
  return `${level.trim()}${stream ? ` ${stream}` : ""}`;
};

/**
 * The name to save after an edit. A name still in its generated form follows a
 * renamed level or stream; a name the school typed itself is kept as it is.
 */
export function nameAfterEdit(before: Pick<Class, "level" | "section">, level: string, section: string, typed: string) {
  const name = typed.trim();
  return !name || name === generatedName(before.level, before.section) ? generatedName(level, section) : name;
}

export type Fill = "over" | "full" | "nearly" | "space" | "empty";

const FILL: Record<Fill, { label: string; dot: string; bar: string }> = {
  over: { label: "Over capacity", dot: "bg-red-500", bar: "bg-red-500" },
  full: { label: "Full", dot: "bg-amber-500", bar: "bg-amber-500" },
  nearly: { label: "Nearly full", dot: "bg-amber-400", bar: "bg-amber-400" },
  space: { label: "Has places", dot: "bg-emerald-500", bar: "bg-emerald-500" },
  empty: { label: "No learners yet", dot: "bg-gray-400", bar: "bg-gray-300" },
};

/** How full a class (or a level) is: over, full, nearly (90%+), space or empty. */
export function occupancy(learners: number, capacity: number) {
  const percent = capacity > 0 ? Math.round((learners / capacity) * 100) : 0;
  const fill: Fill = learners === 0 ? "empty" : learners > capacity ? "over" : learners === capacity ? "full" : percent >= 90 ? "nearly" : "space";
  return { percent, fill, ...FILL[fill] };
}

const fillOf = (row: Class) => occupancy(row.current_student_count, row.capacity).fill;
const inOrder = () => 0;

export const classConfig: RegisterConfig<Class> = {
  search: (row) => [row.name, row.level, row.section, row.class_teacher_name, row.room].filter(Boolean).join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "no_teacher", label: "No class teacher", match: (row) => !row.class_teacher },
    { key: "full", label: "Full or over", match: (row) => ["full", "over"].includes(fillOf(row)) },
    { key: "space", label: "Has places", match: (row) => row.current_student_count < row.capacity },
    { key: "empty", label: "No learners", match: (row) => row.current_student_count === 0 },
  ],
  category: { label: "Level", value: (row) => row.level, inRowOrder: true },
  sorts: [
    { key: "school", label: "School order", compare: inOrder },
    { key: "fullest", label: "Fullest first", compare: reversed(byNumber((row) => (row.capacity ? row.current_student_count / row.capacity : 0))) },
    { key: "places", label: "Most places free", compare: reversed(byNumber((row) => row.available_spots)) },
    { key: "largest", label: "Most learners", compare: reversed(byNumber((row) => row.current_student_count)) },
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
  ],
};

export interface LevelRow {
  level: string;
  classes: Class[];
  streams: string;
  learners: number;
  boys: number;
  girls: number;
  capacity: number;
  free: number;
  withoutTeacher: number;
}

/** Each level with its streams added up, in school order. */
export function levelRows(classes: Class[]): LevelRow[] {
  const levels = new Map<string, Class[]>();
  classes.forEach((row) => levels.set(row.level, [...(levels.get(row.level) ?? []), row]));
  return [...levels.entries()].map(([level, rows]) => {
    const learners = rows.reduce((sum, row) => sum + row.current_student_count, 0);
    const capacity = rows.reduce((sum, row) => sum + row.capacity, 0);
    return {
      level,
      classes: rows,
      streams: rows.map((row) => row.section || "No stream").join(", "),
      learners,
      boys: rows.reduce((sum, row) => sum + (row.boys ?? 0), 0),
      girls: rows.reduce((sum, row) => sum + (row.girls ?? 0), 0),
      capacity,
      // Places in classes with room; an over-full stream doesn't use up another's places.
      free: rows.reduce((sum, row) => sum + Math.max(0, row.available_spots), 0),
      withoutTeacher: rows.filter((row) => !row.class_teacher).length,
    };
  });
}

export const levelConfig: RegisterConfig<LevelRow> = {
  search: (row) => `${row.level} ${row.streams}`,
  views: [
    { key: "all", label: "All" },
    { key: "no_places", label: "No places left", match: (row) => row.free === 0 },
    { key: "no_teacher", label: "Missing a class teacher", match: (row) => row.withoutTeacher > 0 },
  ],
  sorts: [
    { key: "school", label: "School order", compare: inOrder },
    { key: "largest", label: "Most learners", compare: reversed(byNumber((row) => row.learners)) },
    { key: "fullest", label: "Fullest first", compare: reversed(byNumber((row) => (row.capacity ? row.learners / row.capacity : 0))) },
    { key: "places", label: "Most places free", compare: reversed(byNumber((row) => row.free)) },
  ],
};

export interface TeacherRow {
  id: number;
  name: string;
  role: string;
  classes: Class[];
}

/** Every teacher who may lead a class, with the classes they lead this year. */
export function teacherRows(teachers: ClassTeacherCandidate[], classes: Class[]): TeacherRow[] {
  return teachers.map((teacher) => ({
    id: teacher.id,
    name: teacher.full_name,
    role: humanise(teacher.user_type),
    classes: classes.filter((row) => row.class_teacher === teacher.id),
  }));
}

export const teacherConfig: RegisterConfig<TeacherRow> = {
  search: (row) => `${row.name} ${row.role} ${row.classes.map((item) => item.name).join(" ")}`,
  views: [
    { key: "all", label: "All" },
    { key: "leading", label: "Leading a class", match: (row) => row.classes.length > 0 },
    { key: "free", label: "Not leading one", match: (row) => row.classes.length === 0 },
    { key: "several", label: "Leading more than one", match: (row) => row.classes.length > 1 },
  ],
  category: { label: "Role", value: (row) => row.role },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    { key: "most", label: "Most classes", compare: reversed(byNumber((row) => row.classes.length)) },
  ],
};

/** A teacher's choice in a class-teacher list: their name and what they already lead. */
export function teacherOption(teacher: ClassTeacherCandidate, classes: Class[], except?: number) {
  const leads = classes.filter((row) => row.class_teacher === teacher.id && row.id !== except).map((row) => row.name);
  return leads.length ? `${teacher.full_name} · leads ${leads.join(", ")}` : teacher.full_name;
}

export interface StreamDraft {
  key: number;
  section: string;
  name: string;
  capacity: string;
  room: string;
  classTeacher: string;
}

/** What's wrong with a new level's streams before it is sent, one line each. */
export function streamProblems(level: string, drafts: StreamDraft[], existing: Class[]) {
  const problems: string[] = [];
  const names = drafts.map((row) => (row.name.trim() || generatedName(level, row.section)).toLowerCase());
  const sections = drafts.map((row) => row.section.trim().toLowerCase());
  if (drafts.length > 1 && sections.some((section) => !section)) problems.push("Name every stream when the level has more than one.");
  const repeated = [...new Set(sections.filter((section, index) => section && sections.indexOf(section) !== index))];
  const typed = (section: string) => drafts.find((row) => row.section.trim().toLowerCase() === section)!.section.trim();
  if (repeated.length) problems.push(`Stream ${repeated.map((section) => `"${typed(section)}"`).join(", ")} is entered more than once.`);
  const taken = new Set(existing.map((row) => row.name.toLowerCase()));
  const clashes = level.trim() ? names.filter((name) => taken.has(name)) : [];
  if (clashes.length) problems.push(`This year already has ${clashes.map((name) => existing.find((row) => row.name.toLowerCase() === name)?.name).join(", ")}.`);
  return problems;
}
